import {
  BracketsSquare,
  BracketsCurly,
  ListBullets,
  Subtract,
  TextT,
  Plus,
  ArrowsSplit,
  Shuffle,
  FlowArrow,
  Union,
  Tag,
  Funnel,
  Table,
} from "@phosphor-icons/react";
import { IconSize } from "./icons";
import { whiteHex } from "../../lib/constants";
import Event from "./Event";
import IconContainer from "./IconContainer";

export type TypeIconType =
  | "boolean"
  | "string"
  | "int"
  | "float"
  | "enum"
  | "union"
  | "object"
  | "input"
  | "list"
  | "event"
  | "split"
  | "test"
  | "ml"
  | "arm"
  | "tag"
  | "funnel"
  | "table";

export default function TypeIcon({
  type,
  isAddNew,
  size = "medium",
}: {
  type: TypeIconType;
  isAddNew?: boolean;
  size?: IconSize;
}): React.ReactElement {
  switch (type) {
    case "boolean":
      return (
        <IconContainer size={size} className="bg-type-icon-yellow">
          <Subtract color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    case "string":
      return (
        <IconContainer size={size} className="bg-type-icon-light-blue">
          <TextT color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    case "enum":
      return (
        <IconContainer size={size} className="bg-type-icon-yellow">
          {isAddNew ? (
            <Plus color={whiteHex} weight="bold" size={iconSize(size)} />
          ) : (
            <ListBullets color={whiteHex} weight="bold" size={iconSize(size)} />
          )}
        </IconContainer>
      );
    case "union":
      return (
        <IconContainer size={size} className="bg-type-icon-blue">
          {isAddNew ? (
            <Plus color={whiteHex} weight="bold" size={iconSize(size)} />
          ) : (
            <Union color={whiteHex} weight="bold" size={iconSize(size)} />
          )}
        </IconContainer>
      );
    case "object":
      return (
        <IconContainer size={size} className="bg-type-icon-blue">
          {isAddNew ? (
            <Plus color={whiteHex} weight="bold" size={iconSize(size)} />
          ) : (
            <BracketsCurly
              color={whiteHex}
              weight="bold"
              size={iconSize(size)}
            />
          )}
        </IconContainer>
      );
    case "input":
      return (
        <IconContainer size={size} className="bg-type-icon-blue">
          {isAddNew ? (
            <Plus color={whiteHex} weight="bold" size={iconSize(size)} />
          ) : (
            <InputIcon />
          )}
        </IconContainer>
      );
    case "list":
      return (
        <IconContainer size={size} className="bg-type-icon-yellow">
          <BracketsSquare
            color={whiteHex}
            weight="bold"
            size={iconSize(size)}
          />
        </IconContainer>
      );
    case "int":
      return (
        <IconContainer size={size} className="bg-type-icon-green">
          <IntIcon size={size} />
        </IconContainer>
      );
    case "float":
      return (
        <IconContainer size={size} className="bg-type-icon-green">
          <FloatIcon size={size} />
        </IconContainer>
      );
    case "event":
      return (
        <IconContainer size={size} className="bg-intent-success">
          <Event size={iconSize(size)} />
        </IconContainer>
      );
    case "split":
    case "test":
      return (
        <IconContainer size={size} className="bg-base-purple">
          <ArrowsSplit
            size={iconSize(size) - 2}
            color={whiteHex}
            weight="bold"
          />
        </IconContainer>
      );
    case "ml":
      return (
        <IconContainer size={size} className="bg-type-icon-lavender">
          <Shuffle size={iconSize(size) - 2} color={whiteHex} weight="bold" />
        </IconContainer>
      );
    case "arm":
      return (
        <IconContainer size={size} className="bg-type-icon-yellow">
          <FlowArrow color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    case "tag":
      return (
        <IconContainer size={size} className="bg-type-icon-yellow">
          <Tag color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    case "funnel":
      return (
        <IconContainer size={size} className="bg-base-purple">
          <Funnel color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    case "table":
      return (
        <IconContainer size={size} className="bg-type-icon-lavender">
          <Table color={whiteHex} weight="bold" size={iconSize(size)} />
        </IconContainer>
      );
    default:
      throw new Error(`Unknown type icon type: ${type}`);
  }
}

function iconSize(size: IconSize): number {
  switch (size) {
    case "x-small":
      return 10;
    case "small":
      return 12;
    case "medium":
      return 14;
    case "large":
      return 16;
    default: {
      const neverSize: never = size;
      throw new Error(`Unknown icon size: ${neverSize}`);
    }
  }
}

function InputIcon(): React.ReactElement {
  return (
    <svg
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="7.5" y="4" width="2" height="7" rx="1" fill="white" />
      <rect
        x="4"
        y="8.41418"
        width="2"
        height="6"
        rx="1"
        transform="rotate(-45 4 8.41418)"
        fill="white"
      />
      <rect
        x="11.2427"
        y="7"
        width="2"
        height="6"
        rx="1"
        transform="rotate(45 11.2427 7)"
        fill="white"
      />
    </svg>
  );
}

function IntIcon({ size }: { size: IconSize }): React.ReactElement {
  return (
    <svg
      width={iconSize(size)}
      viewBox="0 0 13 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.872 9C0.664 9 0.5 8.944 0.38 8.832C0.268 8.712 0.212 8.552 0.212 8.352C0.212 8.144 0.268 7.988 0.38 7.884C0.5 7.78 0.664 7.728 0.872 7.728H2.216V1.836H2.96L1.196 2.904C1.044 2.992 0.9 3.028 0.764 3.012C0.636 2.988 0.524 2.932 0.428 2.844C0.34 2.748 0.28 2.636 0.248 2.508C0.216 2.38 0.224 2.252 0.272 2.124C0.328 1.988 0.432 1.876 0.584 1.788L2.24 0.792C2.4 0.696 2.56 0.615999 2.72 0.551999C2.88 0.479999 3.028 0.443999 3.164 0.443999C3.332 0.443999 3.472 0.491999 3.584 0.588C3.696 0.675999 3.752 0.823999 3.752 1.032V7.728H4.976C5.184 7.728 5.344 7.78 5.456 7.884C5.576 7.988 5.636 8.144 5.636 8.352C5.636 8.56 5.576 8.72 5.456 8.832C5.344 8.944 5.184 9 4.976 9H0.872ZM7.19131 9C6.93531 9 6.74731 8.944 6.62731 8.832C6.51531 8.712 6.45931 8.54 6.45931 8.316C6.45931 8.18 6.49531 8.048 6.56731 7.92C6.63931 7.792 6.74331 7.656 6.87931 7.512L9.29131 4.944C9.65931 4.56 9.92331 4.212 10.0833 3.9C10.2433 3.58 10.3233 3.26 10.3233 2.94C10.3233 2.54 10.1953 2.236 9.93931 2.028C9.69131 1.82 9.32731 1.716 8.84731 1.716C8.57531 1.716 8.30331 1.756 8.03131 1.836C7.75931 1.908 7.48331 2.036 7.20331 2.22C7.05931 2.316 6.92331 2.356 6.79531 2.34C6.67531 2.324 6.56731 2.272 6.47131 2.184C6.38331 2.096 6.32331 1.988 6.29131 1.86C6.25931 1.732 6.26331 1.604 6.30331 1.476C6.35131 1.34 6.44731 1.224 6.59131 1.128C6.93531 0.895999 7.31931 0.719999 7.74331 0.599999C8.17531 0.48 8.60731 0.42 9.03931 0.42C9.64731 0.42 10.1593 0.512 10.5753 0.696C10.9913 0.88 11.3073 1.148 11.5233 1.5C11.7393 1.852 11.8473 2.28 11.8473 2.784C11.8473 3.12 11.7913 3.448 11.6793 3.768C11.5753 4.088 11.4113 4.412 11.1873 4.74C10.9713 5.068 10.6873 5.416 10.3353 5.784L8.07931 8.136V7.728H11.6073C11.8233 7.728 11.9873 7.78 12.0993 7.884C12.2113 7.988 12.2673 8.144 12.2673 8.352C12.2673 8.56 12.2113 8.72 12.0993 8.832C11.9873 8.944 11.8233 9 11.6073 9H7.19131Z"
        fill="white"
      />
    </svg>
  );
}

function FloatIcon({ size }: { size: IconSize }): React.ReactElement {
  return (
    <svg
      width={iconSize(size)}
      viewBox="0 0 16 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.096 9C0.84 9 0.652 8.944 0.532 8.832C0.42 8.712 0.364 8.54 0.364 8.316C0.364 8.18 0.4 8.048 0.472 7.92C0.544 7.792 0.648 7.656 0.784 7.512L3.196 4.944C3.564 4.56 3.828 4.212 3.988 3.9C4.148 3.58 4.228 3.26 4.228 2.94C4.228 2.54 4.1 2.236 3.844 2.028C3.596 1.82 3.232 1.716 2.752 1.716C2.48 1.716 2.208 1.756 1.936 1.836C1.664 1.908 1.388 2.036 1.108 2.22C0.964 2.316 0.828 2.356 0.7 2.34C0.58 2.324 0.472 2.272 0.376 2.184C0.288 2.096 0.228 1.988 0.196 1.86C0.164 1.732 0.168 1.604 0.208 1.476C0.256 1.34 0.352 1.224 0.496 1.128C0.84 0.895999 1.224 0.719999 1.648 0.599999C2.08 0.48 2.512 0.42 2.944 0.42C3.552 0.42 4.064 0.512 4.48 0.696C4.896 0.88 5.212 1.148 5.428 1.5C5.644 1.852 5.752 2.28 5.752 2.784C5.752 3.12 5.696 3.448 5.584 3.768C5.48 4.088 5.316 4.412 5.092 4.74C4.876 5.068 4.592 5.416 4.24 5.784L1.984 8.136V7.728H5.512C5.728 7.728 5.892 7.78 6.004 7.884C6.116 7.988 6.172 8.144 6.172 8.352C6.172 8.56 6.116 8.72 6.004 8.832C5.892 8.944 5.728 9 5.512 9H1.096ZM7.59531 9.06C7.32331 9.06 7.10331 8.976 6.93531 8.808C6.77531 8.64 6.69531 8.424 6.69531 8.16C6.69531 7.904 6.77531 7.696 6.93531 7.536C7.10331 7.368 7.32331 7.284 7.59531 7.284C7.87531 7.284 8.09131 7.368 8.24331 7.536C8.40331 7.696 8.48331 7.904 8.48331 8.16C8.48331 8.424 8.40331 8.64 8.24331 8.808C8.09131 8.976 7.87531 9.06 7.59531 9.06ZM12.1439 9.12C11.7199 9.12 11.2879 9.064 10.8479 8.952C10.4079 8.84 10.0039 8.668 9.63588 8.436C9.48388 8.34 9.37988 8.228 9.32388 8.1C9.27588 7.964 9.26388 7.832 9.28788 7.704C9.31988 7.568 9.37988 7.456 9.46788 7.368C9.56388 7.272 9.67588 7.216 9.80388 7.2C9.93988 7.184 10.0879 7.22 10.2479 7.308C10.5439 7.476 10.8439 7.604 11.1479 7.692C11.4519 7.78 11.7759 7.824 12.1199 7.824C12.4559 7.824 12.7439 7.764 12.9839 7.644C13.2239 7.524 13.4079 7.356 13.5359 7.14C13.6639 6.924 13.7279 6.672 13.7279 6.384C13.7279 5.936 13.5919 5.576 13.3199 5.304C13.0479 5.024 12.6759 4.884 12.2039 4.884C11.9479 4.884 11.6999 4.928 11.4599 5.016C11.2199 5.096 10.9839 5.24 10.7519 5.448C10.6799 5.512 10.5879 5.576 10.4759 5.64C10.3639 5.696 10.2439 5.724 10.1159 5.724C9.91588 5.724 9.76388 5.672 9.65988 5.568C9.55588 5.464 9.50388 5.32 9.50388 5.136V1.212C9.50388 0.995999 9.55988 0.831999 9.67188 0.719999C9.79188 0.599999 9.95988 0.539999 10.1759 0.539999H14.1239C14.3399 0.539999 14.5039 0.595999 14.6159 0.707999C14.7359 0.812 14.7959 0.968 14.7959 1.176C14.7959 1.376 14.7359 1.532 14.6159 1.644C14.5039 1.756 14.3399 1.812 14.1239 1.812H11.0039V4.5H10.6079C10.8159 4.22 11.0919 4.004 11.4359 3.852C11.7799 3.7 12.1559 3.624 12.5639 3.624C13.0999 3.624 13.5639 3.74 13.9559 3.972C14.3559 4.196 14.6639 4.512 14.8799 4.92C15.1039 5.32 15.2159 5.788 15.2159 6.324C15.2159 6.876 15.0919 7.364 14.8439 7.788C14.5959 8.204 14.2399 8.532 13.7759 8.772C13.3199 9.004 12.7759 9.12 12.1439 9.12Z"
        fill="white"
      />
    </svg>
  );
}
